import { Theme } from '../../consumer/consumer.json.model';
import {
  Config,
  EnumerationPart,
  EnumPlaceholderPart,
  EquationPart,
  GapTablePart,
  GeometryPart,
  LearnItem,
  LearnItemPart,
  LSAPart,
  MengenLegenPart,
  PlayerSettings,
  ReorderClausePart,
  TextPart,
} from '../types/player-types';

export const initialTheme: Theme = {
  primaryColor: '#009EA6',
  secondaryColor: '#00dbca',
  tertiaryColor: '#ffffff',
  placeholderColor: '#f2f2f2',
  hoverColor: '#00dbca',
  drawColor: '#009EA6',
  drawHighlightColor: '#00dbca',
  compareSolutionsBgColor: '#00dbca',
};

export const initialPlayerSetting: PlayerSettings = {
  selfJudgementPopup: false,
  dontKnowBtn: false,
  readoutExerciseText: false,
  randomOrderOfTestSequences: false,
  textSelectionEnabled: false,
  emptyAnswerWarningPopup: false,
  checkAnswerPopup: false,
  showNoteButton: false,
  showBottomPart: false,
  showSelfRating: false,
  showExerciseSequenceTabs: false,
  allowedFrontendOrigins: [],
  hideExerciseHeadline: false,
  theme: initialTheme,
  maxTries: 3,
  retryMode: 'standard',
  hideResetExerciseButton: false,
  showCompletionIcons: true,
  subjectConfigs: [],
  tabLetters: true,
  disableExerciseFeedback: false,
  flattenExercises: false,
  footerInfo: '',
};

export const initialConfig: Config = {
  feedback: [],
  kiService: false,
  playerSettings: initialPlayerSetting,
  resolutions: [1920, 1080],
  sessionTimeoutMinutes: { value: 60 },
};

export const initialLearnItem: LearnItem = {
  id: '',
  subjectId: '',
  consumerId: '',
  name: '',
  kind: 'exam',
  assignments: [],
  assets: {},
  pages: [],
  correct: [],
  config: initialConfig,
  copyRestricted: true,
  consumerData: {},
  approval: 'Pending',
  updated: new Date(),
  changedBy: '',
  lastVersion: '',
  version: 0,
  dateCreated: new Date().toISOString(),
  wrongThings: [],
  isExam: true,
};

export const DEFAULT_TEXT_TOP: TextPart = {
  _t: 'TextPart',
  markup: '<h1>Großer Contentbereich</h1>',
  markdown: '# Großer Contentbereich',
  name: 'content-l'
};

export const DEFAULT_TEXT_BOTTOM: TextPart = {
  _t: 'TextPart',
  markup: '<h1>Unterer Contentbereich</h1>',
  markdown: '# Unterer Contentbereich',
  name: 'content-b'
};

const DEFAULT_ENUM_PLACEHOLDER: EnumPlaceholderPart = {
  _t: 'EnumPlaceholderPart',
  name: '',
  text: '',
  placeholder: [],
  enumeration: []
};

export const DEFAULT_GEOMETRY: GeometryPart = {
  _t: 'GeometryPart',
  name: '',
  initial: [],
  definitions: [],
  coordinateSystem: {
    x: { start: -10, end: 10, marking: 1, markingBoxCount: 2, color: '009EA6', style: { linestyle: 'solid' } },
    y: { start: -10, end: 10, marking: 1, markingBoxCount: 2, color: '009EA6', style: { linestyle: 'solid' } }
  }
};

export const PARTS_BY_KIND: Record<string, LearnItemPart[]> = {
  'auswahlText': [
    { ...DEFAULT_TEXT_TOP },
    { _t: 'EnumerationPart', name: 'enum-mc-text-h', text: '', items: [], align: '' } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'auswahlBild': [
    { ...DEFAULT_TEXT_TOP },
    { _t: 'EnumerationPart', name: 'enum-mc-text-h', text: '', items: [], align: '' } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'auswahl': [
    { ...DEFAULT_TEXT_TOP },
    { _t: 'EnumerationPart', name: 'enum-mc-text-h', text: '', items: [], align: '' } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'buchungstrainer': [
    { ...DEFAULT_TEXT_TOP },
    { _t: 'EnumerationPart', name: 'enum-ph-account-trainer', text: '', items: [], align: 'left' } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'farbwahl-markiertext': [
    { ...DEFAULT_TEXT_TOP },
    { _t: 'EnumerationPart', name: 'mark-colors-text', text: '', items: [], align: '' } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'freitext': [
    { ...DEFAULT_TEXT_TOP },
    { _t: 'EnumerationPart', name: 'enum-ph-mark-gap-text', text: '', items: [], align: '' } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'lsa-frage-zum-text': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'LSAPart',
      name: 'lsa-question-for-text',
      language: 'german',
      sourceText: '',
      idealText: '',
      severity: 0.75,
      contentRelation: 0.35,
      plagiarismBoundary: 0.35,
      contentLengthRelationBoundary: 0.35,
      scoredTexts: {
        text1: { text: '', score: 0.7 },
        text2: { text: '', score: 0.3 }
      }
    } as LSAPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'lsa-zusammenfassung': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'LSAPart',
      name: 'lsa-summary',
      language: 'german',
      sourceText: '',
      idealText: '',
      severity: 0.75,
      contentRelation: 0.35,
      plagiarismBoundary: 0.35,
      contentLengthRelationBoundary: 0.35,
      scoredTexts: {
        text1: { text: '', score: 0.7 },
        text2: { text: '', score: 0.3 }
      }
    } as LSAPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'lueckenbild': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumPlaceholderPart',
      name: 'enum-ph-words-on-image',
      placeholder: [],
      enumeration: []
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'lueckentabelle': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'GapTablePart',
      name: 'enum-ph-gap-table',
      align: 'center',
      items: [],
      tableContent: [
        { cols: [{ cellType: 'blank', content: 'Aufgabe' }, { cellType: 'blank', content: 'Ergebnis' }] },
        { cols: [{ cellType: 'blank', content: '10 + 5 =' }, { cellType: 'answer', content: '', answerId: 'id-1' }] }
      ],
      showBorder: true
    } as GapTablePart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'lueckentext': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumerationPart',
      name: 'enum-ph-gap-text',
      text: '',
      items: [],
      align: 'left'
    } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'matheaufgabe': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EquationPart',
      name: 'equation-solve',
      equationToView: '',
      equationToSolve: '',
      variables: []
    } as EquationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'mengen-legen': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'MengenLegenPart',
      name: 'mengen-legen'
    } as MengenLegenPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'mm-papier': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'GeometryPart',
      name: 'draw-geo-on-mm',
      initial: [],
      definitions: [],
      coordinateSystem: {
        ...DEFAULT_GEOMETRY.coordinateSystem
      }
    } as GeometryPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'reihenfolge-bestimmen': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumerationPart',
      name: 'enum-define-order-h',
      text: '',
      align: '',
      items: []
    } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'satzglieder-umstellen': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'ReorderClausePart',
      name: 'enum-clauses-reordering',
      clauses: [],
      cells: [],
      columns: 4,
      rows: 1,
      headers: []
    } as ReorderClausePart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'silbenboegen': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumerationPart',
      name: 'enum-ph-silbenboegen',
      text: 'Setze die Silbenbögen',
      items: [],
      align: ''
    } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'taschenrechner': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumerationPart',
      name: 'enum-ph-taschenrechner',
      text: '',
      items: [],
      align: ''
    } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'wechselbild': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumPlaceholderPart',
      name: 'enum-ph-images-on-image',
      placeholder: [],
      enumeration: []
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'wechseltext': [
    { ...DEFAULT_TEXT_TOP },
    {
      ...DEFAULT_ENUM_PLACEHOLDER,
      name: 'toggle-words-text'
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'wechseltext-bild': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumPlaceholderPart',
      name: 'enum-ph-toggle-words-on-image',
      placeholder: [],
      enumeration: []
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'wechseltext-grossklein': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumerationPart',
      name: 'toggle-lowercase-words-text',
      text: '',
      items: [],
      align: ''
    } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'winkel-messen': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'GeometryPart',
      name: 'draw-geo-measure-angle',
      initial: [],
      definitions: [],
      coordinateSystem: { ...DEFAULT_GEOMETRY.coordinateSystem }
    } as GeometryPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'winkel-zeichnen': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'GeometryPart',
      name: 'draw-geo-draw-angle',
      initial: [],
      definitions: [],
      coordinateSystem: { ...DEFAULT_GEOMETRY.coordinateSystem }
    } as GeometryPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'wortwahl-markiertext': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumerationPart',
      name: 'mark-words-text',
      text: '',
      items: [],
      align: ''
    } as EnumerationPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'zeichensetzung': [
    { ...DEFAULT_TEXT_TOP },
    {
      ...DEFAULT_ENUM_PLACEHOLDER,
      name: 'enum-ph-toggle-punctuation'
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'zuordnung-bild-dnd': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumPlaceholderPart',
      name: 'enum-ph-dnd-to-image',
      text: '',
      placeholder: [],
      enumeration: []
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'zuordnung-nach-kategorie': [
    { ...DEFAULT_TEXT_TOP },
    {
      ...DEFAULT_ENUM_PLACEHOLDER,
      name: 'enum-ph-dnd-categories'
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'zuordnung-nach-kategorie-aus-text': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumPlaceholderPart',
      name: 'enum-ph-dnd-from-text-h',
      placeholder: [],
      enumeration: []
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ],
  'zuordnung-text-dnd': [
    { ...DEFAULT_TEXT_TOP },
    {
      _t: 'EnumPlaceholderPart',
      name: 'enum-ph-dnd-to-text-v',
      text: '',
      placeholder: [],
      enumeration: []
    } as EnumPlaceholderPart,
    { ...DEFAULT_TEXT_BOTTOM }
  ]
};
